import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ARTICLES } from "../data/journal";
import PogLogo from "../components/PogLogo";

const CANONICAL = "https://pogspirits.com";

const Journal = () => {
  return (
    <div className="min-h-screen bg-[#14100D] text-[#EFE7D6]">
      <Helmet>
        <title>The POG Journal — Stories, Cocktails & Hawaiian Flavor</title>
        <meta
          name="description"
          content="The POG Journal: the story of POG, vodka tasting notes, Hawaiian cocktail recipes, and the rise of island spirits. From POG Spirits — made in partnership with Meadow Gold."
        />
        <link rel="canonical" href={`${CANONICAL}/journal`} />
        <meta property="og:title" content="The POG Journal — Stories, Cocktails & Hawaiian Flavor" />
        <meta property="og:description" content="The story of POG, tasting notes, and Hawaiian cocktail recipes from POG Spirits." />
        <meta property="og:url" content={`${CANONICAL}/journal`} />
        <meta property="og:image" content={`${CANONICAL}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "@id": `${CANONICAL}/journal`,
            name: "The POG Journal",
            url: `${CANONICAL}/journal`,
            description: "Stories, cocktails, and Hawaiian flavor from POG Spirits.",
            publisher: { "@type": "Organization", name: "POG Spirits", url: CANONICAL },
            blogPost: ARTICLES.map((a) => ({
              "@type": "BlogPosting",
              headline: a.title,
              url: `${CANONICAL}/journal/${a.slug}`,
              datePublished: a.date,
              description: a.description,
            })),
          })}
        </script>
      </Helmet>

      <header className="fixed top-0 left-0 right-0 z-50 bg-[#14100D]/90 backdrop-blur-xl border-b border-[#EFE7D6]/10">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <PogLogo size={28} />
            <span className="font-body italic text-[#EFE7D6] tracking-wide">Spirits</span>
          </Link>
          <Link
            to="/"
            className="font-body text-[11px] tracking-[0.28em] uppercase text-[#EFE7D6]/75 hover:text-[#E85A15] transition-colors link-underline"
            data-testid="journal-back-home"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="pt-40 pb-32">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="mb-20"
          >
            <div className="flex items-center gap-6 mb-10">
              <span className="counter">— The Journal</span>
              <span className="w-16 hairline-strong h-px inline-block" />
            </div>
            <h1 className="display-tight text-5xl md:text-8xl mb-8">
              Stories from
              <br />
              <span className="italic text-[#DCCFB4]">the islands.</span>
            </h1>
            <div className="max-w-3xl space-y-6 font-body text-[#DCCFB4]/85 text-lg leading-[1.75] font-light">
              <p>
                The POG Journal is where we collect the story behind Hawai‘i's
                most iconic flavor and the spirits it inspired. POG — passion
                fruit, orange, and guava — was invented at Meadow Gold in 1971,
                and for more than fifty years it has been part of daily life on
                the islands: breakfast tables, plate lunches, beach coolers,
                birthday parties.
              </p>
              <p>
                In 2026, POG Spirits — an independent spirits brand created in
                partnership with Meadow Gold — brings that same flavor into a
                bottle: POG Vodka, a 750 ml, 35% ABV vodka crafted with real
                passion fruit, orange, and guava; and POG Spiked Island Punch,
                a ready-to-drink can at 6% ABV with real fruit and organic
                ingredients. Everything you read here is a chapter of that
                launch — the flavor, the fruits, the recipes, and the moment.
              </p>
              <p>
                Below you'll find the definitive answer to "what is POG?", full
                tasting notes for POG Vodka, three signature cocktails built to
                honor the original, a short guide to why passion fruit, orange,
                and guava work so well together, and a look at the wave of
                Hawaiian spirits shaping the shelf in 2026. Every article is
                written to be useful on its own — read one, share it, or use it
                to introduce someone new to POG. New pieces publish regularly:
                bartender features, retail spotlights, the science of aloha
                flavor, and the occasional postcard from the tasting room.
                Bookmark the page or subscribe via our RSS feed to follow along.
              </p>
              <p className="text-[#DCCFB4]/60 text-sm">
                Written by the POG Spirits team · Hawai‘i · Updated 2026.
                21+ only — please drink responsibly.
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
            {ARTICLES.map((a, i) => (
              <motion.article
                key={a.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`journal-card-${a.slug}`}
              >
                <Link to={`/journal/${a.slug}`} className="group block">
                  <div
                    className="relative aspect-[3/2] overflow-hidden mb-6"
                    style={{ background: "radial-gradient(ellipse at 50% 45%, #2a1f18 0%, #0f0a07 80%)" }}
                  >
                    <img
                      src={a.hero}
                      alt={a.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#14100D]/60 to-transparent" />
                    <span className="absolute top-4 left-4 overline text-[#EFE7D6] text-[10px]">
                      {a.category}
                    </span>
                  </div>
                  <div className="overline text-[#DCCFB4]/50 mb-3 text-[10px]">
                    {a.readMinutes} min read
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight leading-snug mb-3 group-hover:text-[#E85A15] transition-colors">
                    {a.title}
                  </h2>
                  <p className="font-body text-[#DCCFB4]/75 leading-relaxed font-light">
                    {a.excerpt}
                  </p>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Journal;
